import { Avatar, Button, Divider, Stack, Typography } from '@mui/material';
import { CustomDivider } from '../Utils/CustomDivider';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { BigButton, NewIcon, NewLabelIcon } from 'Images';
import { useNavigate } from 'react-router-dom';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { getActivities } from 'API/Home/activities';
import { useLoader } from 'Hooks/useLoader';
import { getPaycheck, getPayslipFile } from 'API/Paychecks/paychecks';
import { downloadFile } from 'Utils';

export interface IActionType {
  name: string;
  iconUrlPath: string;
  internalLink?: string;
  externalLink?: string;
  isPersonal: boolean;
  isNew: boolean;
  priority: number;
  type: number;
}

export const CommonActions = () => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Home).commonActions;
  const navigate = useNavigate();
  const { isLoading, setIsLoading, loader } = useLoader(
    'scale(0.25)',
    undefined,
    undefined,
    false
  );

  const [activitiesList, setActivitiesList] = useState<IActionType[]>();
  const [isMounted, setIsMounted] = useState(true);

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const init = useCallback(async () => {
    setIsLoading(true);
    const resList = await getActivities();

    if (isMounted) {
      if (resList) {
        setActivitiesList(resList);
      }
      setIsLoading(false);
    }
  }, [setIsLoading, isMounted]);

  useEffect(() => {
    setIsMounted(true);
    init();

    return () => {
      setIsMounted(false);
    };
  }, [init, imposterId]);

  const handleClick = async (type: IActionType) => {
    //TODO: get from server a flag isDownload
    if (type.type === 4) {
      const lastPaycheck = await getPaycheck();
      if (lastPaycheck && isMounted) {
        const file = await getPayslipFile(lastPaycheck.fileId);
        if (file)
          downloadFile(file.file, '.pdf', dictionary.lastPaycheck, false);
      }
    } else if (type.externalLink) window.open(type.externalLink);
    else if (type.internalLink) navigate(type.internalLink);
  };

  return (
    <Stack>
      {isDesktop && (
        <>
          <Typography variant='h5' component={'h2'}>
            {dictionary.title}
          </Typography>
          <CustomDivider />
        </>
      )}
      <Stack
        bgcolor={'white'}
        p={isDesktop ? '0 24px 32px' : '0 0 24px'}
        gap={'16px'}
        alignItems={'center'}
        borderRadius={'16px'}
        border={'1px solid'}
        borderColor={isDesktop ? 'primary.shade_100' : 'primary.shade_200'}
        mt={'16px'}
        maxWidth={'1212px'}
        boxShadow={'0px 2px 20px 0px rgba(58, 110, 236, 0.12)'}
      >
        {isLoading ? (
          <Stack height={'40px'} mb={3}>
            {loader}
          </Stack>
        ) : (
          <>
            <Stack
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={isDesktop ? 'space-around' : 'center'}
              gap={isDesktop ? '8px' : '0px 6px'}
              display={isDesktop ? undefined : 'grid'}
              gridTemplateColumns={
                isDesktop ? undefined : '1fr 1px 1fr 1px 1fr'
              }
            >
              {activitiesList &&
                activitiesList.map((type, index) => (
                  <Fragment key={index}>
                    <Button
                      sx={{
                        m: isDesktop ? '16px 0' : '24px auto',
                        p: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        width: isDesktop ? '182px' : '100px',
                        height: isDesktop ? '118px' : undefined,
                        '&:hover': {
                          bgcolor: '#E4C1FF',
                        },
                      }}
                      onClick={() => handleClick(type)}
                    >
                      {type.isNew && (
                        <Avatar
                          sx={
                            isDesktop
                              ? {
                                  width: '46px',
                                  height: '20px',
                                  position: 'absolute',
                                  top: '8px',
                                  right: '0',
                                }
                              : {
                                  width: '41px',
                                  height: '19px',
                                  position: 'absolute',
                                  top: '-17px',
                                  right: '0',
                                }
                          }
                          src={NewIcon}
                          alt=''
                        />
                      )}
                      <Avatar
                        sx={
                          isDesktop
                            ? undefined
                            : { width: '32px', height: '32px' }
                        }
                        src={decodeURIComponent(type.iconUrlPath)}
                        alt=''
                      />
                      <Typography
                        color={'initial'}
                        variant='body5'
                        height={'38px'}
                      >
                        {type.name}
                      </Typography>
                    </Button>
                    {index < activitiesList.length - 1 &&
                      (isDesktop ? true : index !== 2) && (
                        <Divider
                          orientation='vertical'
                          flexItem
                          aria-hidden='true'
                          sx={{
                            borderColor: 'primary.shade_200',
                            m: '24px 0',
                          }}
                        />
                      )}
                  </Fragment>
                ))}
            </Stack>
            <Button
              role='link'
              onClick={() => navigate(dictionary.actionsLobbyUrl)}
              sx={{ p: 0, width: 'fit-content' }}
              endIcon={
                <Avatar
                  sx={{ height: '50px', width: '46px', mt: '6px' }}
                  src={BigButton}
                  alt=''
                />
              }
            >
              <Typography variant='body5'>{dictionary.buttonText}</Typography>
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
};
